import React, { useEffect, useState } from 'react'
import { formatCurrencyValue, getBackendData } from '../../helpers/utils'
import { Address, CalculateOrderResponse, Product, ProductOption } from '../../model'
import IconWithBackground from '../IconWithBackground/IconWithBackground'
import ProductItem from '../ProductItem/ProductItem'
import PackageItem from '../PackageItem/PackageItem'
import ProductOptionsModalContent from '../ProductOptionsModalContent/ProductOptionsModalContent'
import { useModals } from '../../hooks/useModals'
import { ExtendedProduct } from '../../services/api/orders'
import EditButton from '../EditButton/EditButton'
import Invoice3rdParty from './Invoice3rdParty'
import DefaultLoader from '../Loaders/DefaultLoader'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'

interface OrderSummaryProps {
    data: {
        category: number
        address: Address
        products: Product[]
        invoice3rdParty?: boolean
        invoiceDetails?: {
            name: string
            emailAddress: string
            street: string
            city: string
            extension?: string
            zipcode: string
            houseNumber: string
        }
        remarks?: string
    }
    handleProductSelectionChange(products: Product[]): void
    removeProduct(product: Product): void
    onRemarkChange(remark: string): void
    onInvoiceChange?(isActive: boolean, values: any, isValid: boolean): void
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
    data,
    handleProductSelectionChange,
    removeProduct,
    onRemarkChange,
    onInvoiceChange
}: OrderSummaryProps) => {
    const { showModal, RenderModals } = useModals([
        {
            id: 'productOptions',
            comp: ProductOptionsModalContent,
            props: {
                onConfirm: (product: ExtendedProduct, selectedOptions: ProductOption[]) => {
                    let productToAdd = null

                    if (product._isPack) {
                        productToAdd = {
                            ...product,
                            // @ts-ignore
                            products: product.products?.map((p) => ({
                                ...p,
                                availableOptions: p.availableOptions?.map((o: any) => ({
                                    ...o,
                                    _isAdded: selectedOptions.includes(o)
                                }))
                            }))
                        }
                    } else {
                        productToAdd = {
                            ...product,
                            availableOptions: product.availableOptions?.map((o) => ({
                                ...o,
                                _isAdded: selectedOptions.includes(o)
                            }))
                        }
                    }

                    const newDataProducts = data.products.map((p) => {
                        if (p.id === productToAdd.id) {
                            return productToAdd
                        }
                        return p
                    })
                    handleProductSelectionChange(newDataProducts)
                }
            }
        }
    ])

    const [orderTotalPrice, setOrderTotalPrice] = useState('0,00')
    const [extraOptions, setExtraOptions] = useState<any>([])
    // Can be 'yes', 'no' or 'na' (not available)
    const [thirdPartyInvoiceEnabled, setThirdPartyInvoiceEnabled] =
        useState<CalculateOrderResponse['allowInvoice3RDParty']>('na')
    const [loading, setLoading] = useState(true)

    const fetchTotalPrice = async () => {
        setLoading(true)
        const results = await getBackendData(data)
        setOrderTotalPrice(results[0]?.totalPrice.toFixed(2) || '0,00')
        setExtraOptions(results[0]?.options)
        setThirdPartyInvoiceEnabled(results[0]?.allowInvoice3RDParty || 'na')
        setLoading(false)
    }

    useEffect(() => {
        fetchTotalPrice()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.products])

    return (
        <div className='flex flex-col'>
            <RenderModals />

            {loading ? (
                <DefaultLoader />
            ) : (
                <div className='rounded-xl shadow-sm flex-1 border-full bg-white mt-4'>
                    <header className='pb-3 px-4 pt-4 border-[--tb-fadedPrimary]'>
                        <h2 className='text-xl mb-0'>Controleer je bestelling</h2>
                    </header>
                    <div className='px-4 py-2'>
                        {data.address && (
                            <>
                                <h3 className='text-sm mb-1'>Adres</h3>
                                <div className='flex items-start my-2 pb-3'>
                                    <IconWithBackground size='small' iconName='house' />
                                    <div className='flex flex-col text-xs ml-3'>
                                        <span>
                                            {data.address.street} {data.address.houseNumber}
                                            {data.address.extension ?? ''}
                                        </span>
                                        <span>
                                            {data.address.city}, {data.address.zipcode}
                                        </span>
                                        {data.address.surface && (
                                            <span>
                                                Woonoppervlakte: {data.address.surface} m<sup>2</sup>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div className='mb-4'>
                        <h3 className='mb-1 px-4'>Jouw producten</h3>
                        {data.products.length > 0 ? (
                            <>
                                {data.products.map((product: any, index: number) => {
                                    const hasOptions =
                                        product.availableOptions?.length > 0 ||
                                        product.products?.filter((p: any) => p.availableOptions?.length > 0).length > 0

                                    return (
                                        <div className='flex items-center gap-x-2' key={`${index}-${product.id}`}>
                                            {product._isPack ? (
                                                <PackageItem layout='row' a={product} />
                                            ) : (
                                                <ProductItem
                                                    showHighlight={false}
                                                    showOptions
                                                    product={product}
                                                    productInBasket={product}
                                                />
                                            )}

                                            <div className='flex flex-col sm:flex-row gap-2'>
                                                {hasOptions ? (
                                                    <EditButton
                                                        onClick={() =>
                                                            showModal('productOptions', {
                                                                product,
                                                                isPack: product._isPack
                                                            })
                                                        }
                                                    />
                                                ) : (
                                                    <div className='w-8 flex-shrink-0'></div>
                                                )}

                                                <button
                                                    onClick={() => removeProduct(product)}
                                                    className='primaryBtn mr-4 flex-shrink-0'
                                                >
                                                    <DynamicFaIcon iconName='times' />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                                {extraOptions &&
                                    extraOptions.length > 0 &&
                                    extraOptions.map((a: any, index: number) => (
                                        <div key={index} className='flex items-center gap-x-2'>
                                            <div className='w-full flex flex-grow px-4 justify-between items-center py-3 '>
                                                <div className='flex items-center'>
                                                    <span className='italic text-sm ml-16'>{a.name}:</span>
                                                </div>
                                                <div className='space-x-4 flex items-center'>
                                                    <span className='px-4 text-sm'>{formatCurrencyValue(a.price)}</span>
                                                </div>
                                            </div>
                                            <div className='w-16 sm:w-24'></div>
                                        </div>
                                    ))}

                                <div className='pl-6 pr-24 text-base py-2 border-[--tb-fadedPrimary] text-right'>
                                    <span className='font-semibold text-sm'>Totaal: {orderTotalPrice}</span>
                                    <span className='text-xs font-normal text-gray-500 ml-1'>ex BTW</span>
                                </div>
                            </>
                        ) : (
                            <p className='text-sm text-gray-500 px-4'>Geen producten gevonden</p>
                        )}
                    </div>
                    {thirdPartyInvoiceEnabled !== 'na' && (
                        <Invoice3rdParty
                            isChecked={data.invoice3rdParty}
                            defaultValues={{ ...data.invoiceDetails }}
                            isEnabled={thirdPartyInvoiceEnabled === 'yes'}
                            onChange={onInvoiceChange}
                        />
                    )}

                    <div className='p-4 md:px-6 border-top w-full'>
                        <div className='font-semibold text-sm'>Plaats extra opmerking</div>
                        <textarea
                            className='w-full md:w-2/3 border-full rounded-lg p-2 mt-2 focus:outline-none'
                            rows={3}
                            placeholder='Extra opmerking...'
                            style={{ resize: 'none' }}
                            onChange={(e) => onRemarkChange(e.target.value)}
                            defaultValue={''}
                            value={data.remarks}
                        ></textarea>
                    </div>
                </div>
            )}
        </div>
    )
}

export default OrderSummary
